import type { DealProductBuilderRow, FloatingRateLoanDealProductBuilderRow } from '@src/types/OTC';
import { floatingRateLoanFieldDefinitions } from '@src/utils/OTC/floatingRateLoanFieldDefinitions';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import type { useDisclosureWithContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { useDealProductBuilderAssets } from '../DealProductBuilderAssetsProvider';
import type { DealProductBuilderColumn } from '../dealProductBuilderColDef';
import { DealProductBuilderGrid } from './DealProductBuilderGrid';
import { buildCurrencySelectorColumn } from './baseProductBuilderColumns';

interface FloatingRateLoanProductBuilderGridProps {
  panelID: string;
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >;
}

export const FloatingRateLoanProductBuilderGrid = ({
  panelID,
  productDetailsDisclosure,
}: FloatingRateLoanProductBuilderGridProps) => {
  const { availableBorrowedAssets, availableCollateralAssets } = useDealProductBuilderAssets();

  const customColumns = useMemo(() => {
    const borrowedAssetColumn = buildCurrencySelectorColumn<FloatingRateLoanDealProductBuilderRow>({
      key: 'baseAsset',
      headerName: getColumnTitle(floatingRateLoanFieldDefinitions.borrowedAsset),
      currenciesToInclude: availableBorrowedAssets,
      description: getColumnTooltip(floatingRateLoanFieldDefinitions.borrowedAsset) ?? '',
    });
    const collateralAssetColumn = buildCurrencySelectorColumn<FloatingRateLoanDealProductBuilderRow>({
      key: 'collateralAsset',
      headerName: getColumnTitle(floatingRateLoanFieldDefinitions.collateralAsset),
      currenciesToInclude: availableCollateralAssets,
      description: getColumnTooltip(floatingRateLoanFieldDefinitions.collateralAsset) ?? '',
    });
    return [borrowedAssetColumn, collateralAssetColumn] as DealProductBuilderColumn[];
  }, [availableBorrowedAssets, availableCollateralAssets]);

  return (
    <DealProductBuilderGrid
      panelID={panelID}
      productDetailsDisclosure={productDetailsDisclosure}
      customColumnDefs={customColumns}
      fieldDefinitions={floatingRateLoanFieldDefinitions}
    />
  );
};
